import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Pencil, Save, Trash2 } from 'lucide-react';
import { api } from '../../services/api';
import senerfiFormato from '../../assets/images/Senerfi-formato.png';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

const IS_SIG_MOBILE = String(process.env.REACT_APP_APP_VARIANT || '').trim().toLowerCase() === 'sig_mobile';

const MONTH_OPTIONS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const FORM_CODE = 'ET-AMB-090';
const FORM_VERSION = '0';

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

function monthFromDate(value) {
  const raw = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '';
  const idx = Number(raw.slice(5, 7)) - 1;
  return MONTH_OPTIONS[idx] || '';
}

function yearFromDate(value) {
  const raw = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '';
  return raw.slice(0, 4);
}

function emptyForm() {
  const date = todayYmd();
  return {
    id: '',
    inspection_type: 'Inspección',
    inspection_date: date,
    inspection_month: monthFromDate(date),
    inspection_year: yearFromDate(date),
    process_name: '',
    waste_name: '',
    waste_type: '',
    respel_current: '',
    waste_class: '',
    strategy: '',
    quantity_kg: '',
    manager_name: '',
    valid_license: '',
    certificate: '',
    notes: '',
  };
}

function fmtDateDisplay(value) {
  const raw = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw || '-';
  return `${raw.slice(8, 10)}/${raw.slice(5, 7)}/${raw.slice(0, 4)}`;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

async function assetToDataUrl(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export default function GestionResiduosAmbientalModule({ location }) {
  const [form, setForm] = useState(emptyForm);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');

  const title = useMemo(() => {
    if (location === 'popayan') return 'Popayán';
    if (location === 'santander-de-quilichao') return 'Santander de Quilichao';
    return location || 'Sede';
  }, [location]);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.sgi.waste.list({ location, limit: 1000, order: 'desc' });
      const data = Array.isArray(res?.data) ? res.data : [];
      setRows(data);
    } catch (err) {
      console.error('Error cargando gestión de residuos', err);
      setFeedback('No se pudo cargar la información de gestión de residuos.');
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const onDateChange = (value) => {
    setForm((prev) => ({
      ...prev,
      inspection_date: value,
      inspection_month: monthFromDate(value) || prev.inspection_month,
      inspection_year: yearFromDate(value) || prev.inspection_year,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.inspection_date || !form.process_name.trim() || !form.waste_name.trim()) {
      setFeedback('Completa fecha, proceso y nombre del residuo.');
      return;
    }
    try {
      setSaving(true);
      setFeedback('');
      await api.sgi.waste.upsert({
        ...form,
        location,
      });
      setForm(emptyForm());
      setFeedback('Registro guardado correctamente.');
      await loadRows();
    } catch (err) {
      console.error('Error guardando gestión de residuos', err);
      setFeedback(err?.message || 'No se pudo guardar el registro.');
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (row) => {
    setForm({
      id: row.id || '',
      inspection_type: row.inspection_type || 'Inspección',
      inspection_date: row.inspection_date || '',
      inspection_month: row.inspection_month || '',
      inspection_year: row.inspection_year || '',
      process_name: row.process_name || '',
      waste_name: row.waste_name || '',
      waste_type: row.waste_type || '',
      respel_current: row.respel_current || '',
      waste_class: row.waste_class || '',
      strategy: row.strategy || '',
      quantity_kg: row.quantity_kg ?? '',
      manager_name: row.manager_name || '',
      valid_license: row.valid_license || '',
      certificate: row.certificate || '',
      notes: row.notes || '',
    });
    setFeedback(`Editando registro #${row.id}.`);
  };

  const onDelete = async (row) => {
    const ok = window.confirm(`¿Eliminar el registro de residuos #${row.id}?`);
    if (!ok) return;
    try {
      await api.sgi.waste.delete({ id: row.id });
      setFeedback('Registro eliminado.');
      if (String(form.id) === String(row.id)) setForm(emptyForm());
      await loadRows();
    } catch (err) {
      console.error('Error eliminando registro de residuos', err);
      setFeedback(err?.message || 'No se pudo eliminar el registro.');
    }
  };

  const onCancelEdit = () => {
    setForm(emptyForm());
    setFeedback('');
  };

  const exportExcel = async () => {
    try {
      const logoDataUrl = await assetToDataUrl(senerfiFormato);
      const bodyRows = rows.map((row) => `
        <tr>
          <td>${escapeHtml(fmtDateDisplay(row.inspection_date))}</td>
          <td>${escapeHtml(row.inspection_month || '')}</td>
          <td>${escapeHtml(row.inspection_year || '')}</td>
          <td>${escapeHtml(row.process_name || '')}</td>
          <td>${escapeHtml(row.waste_name || '')}</td>
          <td>${escapeHtml(row.waste_type || '')}</td>
          <td>${escapeHtml(row.respel_current || '')}</td>
          <td>${escapeHtml(row.waste_class || '')}</td>
          <td>${escapeHtml(row.strategy || '')}</td>
          <td>${escapeHtml(row.quantity_kg ?? '')}</td>
          <td>${escapeHtml(row.manager_name || '')}</td>
          <td>${escapeHtml(row.valid_license || '')}</td>
          <td>${escapeHtml(row.certificate || '')}</td>
        </tr>
      `).join('');

      const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:x="urn:schemas-microsoft-com:office:excel"
              xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <meta charset="UTF-8" />
            <!--[if gte mso 9]><xml>
              <x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
                <x:Name>Gestion residuos</x:Name>
                <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
              </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook>
            </xml><![endif]-->
            <style>
              body { font-family: Arial, sans-serif; }
              table { border-collapse: collapse; width: 100%; table-layout: fixed; }
              td, th { border: 1px solid #000; padding: 6px 8px; font-size: 11pt; vertical-align: middle; }
              .title { text-align: center; font-weight: 700; }
              .meta-label { font-weight: 700; text-align: left; width: 180px; }
              .meta-value { text-align: right; width: 120px; }
              .header { background: #f4bf16; font-weight: 700; text-align: center; }
              .logo-cell { text-align: center; }
              .logo-cell img { max-width: 220px; max-height: 80px; }
              .w-fecha { width: 90px; }
              .w-mes { width: 95px; }
              .w-ano { width: 70px; }
              .w-proceso { width: 160px; }
              .w-residuo { width: 220px; }
              .w-tipo { width: 150px; }
              .w-respel { width: 140px; }
              .w-clase { width: 130px; }
              .w-estrategia { width: 150px; }
              .w-cantidad { width: 120px; }
              .w-gestor { width: 150px; }
              .w-licencia { width: 210px; }
              .w-cert { width: 120px; }
            </style>
          </head>
          <body>
            <table>
              <colgroup>
                <col span="1" class="w-fecha" />
                <col span="1" class="w-mes" />
                <col span="1" class="w-ano" />
                <col span="1" class="w-proceso" />
                <col span="1" class="w-residuo" />
                <col span="1" class="w-tipo" />
                <col span="1" class="w-respel" />
                <col span="1" class="w-clase" />
                <col span="1" class="w-estrategia" />
                <col span="1" class="w-cantidad" />
                <col span="1" class="w-gestor" />
                <col span="1" class="w-licencia" />
                <col span="1" class="w-cert" />
              </colgroup>
              <tr>
                <td class="logo-cell" colspan="3" rowspan="3"><img src="${logoDataUrl}" alt="Senerfi" /></td>
                <td class="title" colspan="7">SOLUCIONES ENERGETICAS FORESTALES INTEGRALES SAS</td>
                <td class="meta-label" colspan="2">CÓDIGO:</td>
                <td class="meta-value" colspan="1">${escapeHtml(FORM_CODE)}</td>
              </tr>
              <tr>
                <td class="title" colspan="7">SISTEMA DE GESTIÓN AMBIENTAL</td>
                <td class="meta-label" colspan="2">VERSIÓN:</td>
                <td class="meta-value" colspan="1">${escapeHtml(FORM_VERSION)}</td>
              </tr>
              <tr>
                <td class="title" colspan="7">GESTIÓN DE RESIDUOS</td>
                <td class="meta-label" colspan="2">FECHA:</td>
                <td class="meta-value" colspan="1">${escapeHtml(fmtDateDisplay(todayYmd()))}</td>
              </tr>
              <tr>
                <th class="header">FECHA</th>
                <th class="header">MES</th>
                <th class="header">AÑO</th>
                <th class="header">PROCESO</th>
                <th class="header">NOMBRE RESIDUO</th>
                <th class="header">TIPO DE RESIDUO</th>
                <th class="header">CORRIENTE RESPEL</th>
                <th class="header">CLASE</th>
                <th class="header">ESTRATEGIA</th>
                <th class="header">CANTIDAD (KG)</th>
                <th class="header">GESTOR</th>
                <th class="header">LICENCIA / PERMISO / AUTORIZACIÓN VIGENTE</th>
                <th class="header">CERTIFICADO</th>
              </tr>
              ${bodyRows || '<tr><td colspan="13" style="text-align:center;">Sin registros</td></tr>'}
            </table>
          </body>
        </html>
      `;

      const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `gestion_residuos_${location || 'sede'}_${todayYmd()}.xls`;
      document.body.appendChild(link);
      void recordOperationsDownload({ fileType: 'xls', moduleKey: 'sgi', reportName: 'Gestión de residuos' });
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando gestión de residuos', err);
      setFeedback('No se pudo exportar el archivo de Excel con formato.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-base font-semibold text-gray-800">Gestión de residuos</div>
          <div className="text-sm text-gray-600">Inspección y seguimiento de residuos generados en {title}.</div>
        </div>
        {!IS_SIG_MOBILE && (
          <button
            type="button"
            onClick={exportExcel}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white border shadow-sm hover:brightness-110"
            style={{ background: 'linear-gradient(90deg, #059669 0%, #16A34A 100%)', borderColor: '#047857', color: '#FFFFFF' }}
          >
            <Download size={16} /> Exportar Excel
          </button>
        )}
      </div>

      <form onSubmit={onSubmit} className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Tipo inspección</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={form.inspection_type}
              onChange={(e) => setForm((prev) => ({ ...prev, inspection_type: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Fecha</label>
            <input
              type="date"
              className="w-full border rounded px-3 py-2"
              value={form.inspection_date}
              onChange={(e) => onDateChange(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Mes</label>
            <select
              className="w-full border rounded px-3 py-2 bg-white"
              value={form.inspection_month}
              onChange={(e) => setForm((prev) => ({ ...prev, inspection_month: e.target.value }))}
            >
              <option value="">Seleccione...</option>
              {MONTH_OPTIONS.map((month) => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Año</label>
            <input
              type="number"
              className="w-full border rounded px-3 py-2"
              value={form.inspection_year}
              onChange={(e) => setForm((prev) => ({ ...prev, inspection_year: e.target.value }))}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Proceso</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={form.process_name}
              onChange={(e) => setForm((prev) => ({ ...prev, process_name: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Nombre residuo</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={form.waste_name}
              onChange={(e) => setForm((prev) => ({ ...prev, waste_name: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tipo de residuo</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={form.waste_type}
              onChange={(e) => setForm((prev) => ({ ...prev, waste_type: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Corriente RESPEL</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={form.respel_current}
              onChange={(e) => setForm((prev) => ({ ...prev, respel_current: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Clase</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={form.waste_class}
              onChange={(e) => setForm((prev) => ({ ...prev, waste_class: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Estrategia</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={form.strategy}
              onChange={(e) => setForm((prev) => ({ ...prev, strategy: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Cantidad (Kg)</label>
            <input
              type="number"
              step="0.01"
              className="w-full border rounded px-3 py-2"
              value={form.quantity_kg}
              onChange={(e) => setForm((prev) => ({ ...prev, quantity_kg: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Gestor</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={form.manager_name}
              onChange={(e) => setForm((prev) => ({ ...prev, manager_name: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Licencia / permiso / autorización vigente</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={form.valid_license}
              onChange={(e) => setForm((prev) => ({ ...prev, valid_license: e.target.value }))}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Certificado</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={form.certificate}
              onChange={(e) => setForm((prev) => ({ ...prev, certificate: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Observaciones</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className={`text-sm ${feedback.includes('correctamente') || feedback.includes('eliminado') ? 'text-emerald-700' : 'text-yellow-700'}`}>
            {feedback || 'Registra y consulta la gestión de residuos por sede.'}
          </div>
          <div className="flex items-center gap-2">
            {form.id ? (
              <button
                type="button"
                onClick={onCancelEdit}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              >
                Cancelar edición
              </button>
            ) : null}
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            >
              <Save size={16} /> {saving ? 'Guardando...' : (form.id ? 'Actualizar' : 'Guardar')}
            </button>
          </div>
        </div>
      </form>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
          <div className="font-medium text-gray-800">Registros guardados</div>
          <div className="text-sm text-gray-500">{loading ? 'Cargando...' : `${rows.length} registros`}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-yellow-400 text-gray-900">
              <tr>
                <th className="px-3 py-2 text-left">Fecha</th>
                <th className="px-3 py-2 text-left">Mes</th>
                <th className="px-3 py-2 text-left">Año</th>
                <th className="px-3 py-2 text-left">Proceso</th>
                <th className="px-3 py-2 text-left">Nombre residuo</th>
                <th className="px-3 py-2 text-left">Tipo residuo</th>
                <th className="px-3 py-2 text-left">Corriente RESPEL</th>
                <th className="px-3 py-2 text-left">Clase</th>
                <th className="px-3 py-2 text-left">Estrategia</th>
                <th className="px-3 py-2 text-left">Cantidad (Kg)</th>
                <th className="px-3 py-2 text-left">Gestor</th>
                <th className="px-3 py-2 text-left">Licencia vigente</th>
                <th className="px-3 py-2 text-left">Certificado</th>
                <th className="px-3 py-2 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={14} className="px-3 py-6 text-center text-gray-500">
                    No hay registros de gestión de residuos para esta sede.
                  </td>
                </tr>
              ) : rows.map((row) => (
                <tr key={row.id} className="border-t">
                  <td className="px-3 py-2">{fmtDateDisplay(row.inspection_date)}</td>
                  <td className="px-3 py-2">{row.inspection_month || '-'}</td>
                  <td className="px-3 py-2">{row.inspection_year || '-'}</td>
                  <td className="px-3 py-2">{row.process_name || '-'}</td>
                  <td className="px-3 py-2">{row.waste_name || '-'}</td>
                  <td className="px-3 py-2">{row.waste_type || '-'}</td>
                  <td className="px-3 py-2">{row.respel_current || '-'}</td>
                  <td className="px-3 py-2">{row.waste_class || '-'}</td>
                  <td className="px-3 py-2">{row.strategy || '-'}</td>
                  <td className="px-3 py-2">{row.quantity_kg ?? '-'}</td>
                  <td className="px-3 py-2">{row.manager_name || '-'}</td>
                  <td className="px-3 py-2">{row.valid_license || '-'}</td>
                  <td className="px-3 py-2">{row.certificate || '-'}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onEdit(row)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50"
                      >
                        <Pencil size={14} /> Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(row)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={14} /> Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
